import { useState } from 'react'
import { Check, Pencil, Plus, Trash2 } from 'lucide-react'

import type { CreatePlanRequest, PatchPlanRequest, Plan as ContractPlan } from '@cattle/contracts'

import {
  BarList,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  LoadingSkeleton,
  Modal,
  Money,
  MoneyInput,
  PageHeader,
  QueryBoundary,
  Section,
  Select,
  StatusBadge,
  TextInput,
  Toggle,
  type BarListItem,
} from '../../../components'
import {
  PLAN_CODES,
  PLAN_FEATURES,
  queryKeys,
  usePaymentMix,
  usePlans,
  type Plan,
  type PlanCode,
  type PlanFeature,
} from '../../../fixtures'
import { del, patch, post } from '../../../lib/apiClient'
import { t } from '../../../lib/i18n'
import { useApiMutation } from '../../../lib/mutations'

/**
 * P-05 Plans.
 *
 * The route is PLATFORM_OWNER-only and the router already refuses it for
 * support, so nothing here gates a second time — everyone reading this screen
 * can price a plan.
 *
 * ---------------------------------------------------------------------------
 * ONE FORM, AND IT SENDS ONLY WHAT CHANGED
 * ---------------------------------------------------------------------------
 * Edit used to be a single field, the amount, while the whole form existed for
 * Create alone — so a plan named or limited wrongly on the day it was made
 * stayed that way. Both now build the same fields, and the same reading of what
 * a plan is.
 *
 * The patch carries the CHANGED fields and nothing else. That is not tidiness:
 * `patchPlanRequest` was derived from `createPlanRequest.partial()`, and
 * `.partial()` keeps a `.default()`, so a patch of `{ pricePaisa }` arrived at
 * the server carrying `features: []` and emptied the plan's feature list — the
 * very list `requireFeature` reads on every request in every book on that plan.
 * The schema is fixed, and sending a diff means this screen could not do it
 * again even if it were not.
 *
 * A price is still not edited in place. When the amount moves on a plan that
 * books are already paying, the save goes through a confirm that states the
 * count first, because the number of traders affected is the fact that makes
 * the decision.
 *
 * ---------------------------------------------------------------------------
 * RETIRE, NOT DELETE
 * ---------------------------------------------------------------------------
 * `DELETE /platform/plans/:id` is a soft delete and refuses while any live book
 * is on the plan. The count is on screen already, so this offers the right act
 * rather than a button that will be refused: a plan in use is taken OFF SIGNUP
 * (`isPublic: false`) — the books on it keep it, and nobody new can choose it.
 */

const GB = 1024 * 1024 * 1024

/**
 * A limit box. Blank is `null` — UNLIMITED, never zero, which would mean "none
 * allowed" and is a much worse statement. `undefined` is "that is not a number
 * yet", which keeps the save button off rather than sending it.
 */
function parseLimit(text: string): number | null | undefined {
  const trimmed = text.trim()
  if (trimmed === '') return null
  const value = Number(trimmed)
  return Number.isInteger(value) && value >= 0 ? value : undefined
}

/** The same, in GB, because a storage quota is quoted in GB and stored in bytes. */
function parseStorage(text: string): number | null | undefined {
  const trimmed = text.trim()
  if (trimmed === '') return null
  const value = Number(trimmed)
  return Number.isFinite(value) && value >= 0 ? Math.round(value * GB) : undefined
}

/** A whole number for the ladder position. Blank is not a position. */
function parseOrder(text: string): number | undefined {
  const value = Number(text.trim())
  return text.trim() !== '' && Number.isInteger(value) && value >= 0 ? value : undefined
}

/**
 * A limit back into its box. `null` is unlimited and shows as blank — the same
 * blank `parseLimit` reads back as `null`, so opening the form and saving it
 * unchanged sends nothing.
 */
const limitText = (value: number | null): string => (value === null ? '' : String(value))

/**
 * Gigabytes into a box a person can edit. `toGb` divides, so a quota that is
 * not a round number of gigabytes would otherwise arrive as sixteen decimals.
 */
const storageText = (gb: number | null): string =>
  gb === null ? '' : String(Number(gb.toFixed(3)))

/** Same members, order ignored — a feature list is a set, not a sequence. */
const sameFeatures = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join()

/** Everything the edit form holds, before any of it is known to be valid. */
export interface PlanDraft {
  code: PlanCode | null
  name: string
  pricePaisa: number | undefined
  maxAnimals: number | null | undefined
  maxUsers: number | null | undefined
  maxStorageBytes: number | null | undefined
  storageText: string
  features: PlanFeature[]
  isPublic: boolean
  sortOrder: number | undefined
}

/**
 * What actually changed, and nothing else.
 *
 * Exported so it can be tested on its own, because this is the exact shape of a
 * bug that already happened once: `patchPlanRequest` was derived from
 * `createPlanRequest.partial()`, `.partial()` keeps a `.default()`, and a patch
 * of `{ pricePaisa }` therefore reached the server carrying `features: []` —
 * emptying the feature list `requireFeature` reads on every request in every
 * book on that plan. The schema is fixed; sending a diff means this screen
 * cannot resend a field the reader never touched even if it were not.
 *
 * Storage is compared as the TEXT in the box, not as bytes: `toGb` divides, so
 * round-tripping a quota through gigabytes and back does not always land on the
 * same integer, and a form opened and closed untouched must send nothing.
 */
export function planChanges(plan: Plan, draft: PlanDraft): PatchPlanRequest {
  return {
    ...(draft.code !== null && draft.code !== plan.code ? { code: draft.code } : {}),
    ...(draft.name.trim() !== plan.name ? { name: draft.name.trim() } : {}),
    ...(draft.pricePaisa !== undefined && draft.pricePaisa !== plan.pricePaisa
      ? { pricePaisa: draft.pricePaisa }
      : {}),
    ...(draft.maxAnimals !== undefined && draft.maxAnimals !== plan.animalLimit
      ? { maxAnimals: draft.maxAnimals }
      : {}),
    ...(draft.maxUsers !== undefined && draft.maxUsers !== plan.peopleLimit
      ? { maxUsers: draft.maxUsers }
      : {}),
    ...(draft.maxStorageBytes !== undefined && storageText(plan.storageGb) !== draft.storageText.trim()
      ? { maxStorageBytes: draft.maxStorageBytes }
      : {}),
    ...(sameFeatures(draft.features, plan.featureCodes) ? {} : { features: draft.features }),
    ...(draft.isPublic !== plan.isPublic ? { isPublic: draft.isPublic } : {}),
    ...(draft.sortOrder !== undefined && draft.sortOrder !== plan.sortOrder
      ? { sortOrder: draft.sortOrder }
      : {}),
  }
}

/** Local to this screen on purpose — a plan card is not a shared primitive. */
function PlanCard({
  plan,
  featured,
  onEdit,
}: {
  plan: Plan
  featured: boolean
  onEdit: () => void
}) {
  /* A trial is free for a fixed number of days. It has no monthly price, and
     `<Money paisa={0}>` would quote one. */
  const isTrial = plan.trialDays !== undefined

  return (
    <Card surface={featured ? 'accent' : 'heavy'} className="h-full">
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-2">
          <span className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="font-display text-[11px] font-bold tracking-[0.08em] text-muted uppercase">
              {plan.name}
            </span>
            {/* A plan can be live and unlisted at the same time — that is what
                retiring one in use amounts to — so the card has to say which. */}
            {plan.isPublic ? null : <StatusBadge tone="warn">{t('p.plans.hidden')}</StatusBadge>}
          </span>
          <Button
            size="sm"
            variant="ghost"
            icon={<Pencil className="size-3.5" />}
            onClick={onEdit}
            className="-me-2"
          >
            {t('common.edit')}
          </Button>
        </div>

        <div className="mt-1 flex items-baseline gap-1">
          {isTrial ? (
            <span className="font-display text-[26px] leading-tight font-extrabold tracking-[-0.8px] text-dark">
              {t('p.plans.free')}
            </span>
          ) : (
            <Money
              paisa={plan.pricePaisa}
              className="text-[26px] leading-tight font-extrabold tracking-[-0.8px]"
            />
          )}
          <small className="font-display text-[12.5px] font-semibold text-muted">
            {isTrial ? t('p.plans.trialLength', { days: plan.trialDays ?? 0 }) : t('unit.perMonth')}
          </small>
        </div>

        <div className="mt-1 flex flex-wrap items-baseline gap-1 text-[12px] text-muted">
          {isTrial ? (
            t('p.plans.onTrialNow', { count: plan.businesses })
          ) : (
            <>
              <span>{t('p.plans.businessesCount', { count: plan.businesses })}</span>
              <span aria-hidden="true">·</span>
              <Money paisa={plan.revenuePaisa} className="text-[12px] font-bold text-muted" />
            </>
          )}
        </div>

        <ul className="mt-3.5 flex flex-col gap-2 border-t border-muted/15 pt-3.5">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-[12.5px] leading-snug text-sub">
              <Check className="mt-px size-3.5 shrink-0 text-accent-dark" aria-hidden="true" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  )
}

export function PlansScreen() {
  const plans = usePlans()
  const mix = usePaymentMix()

  /** `null` is "no form open"; otherwise a new plan, or the one being edited. */
  const [editing, setEditing] = useState<Plan | null | 'new'>(null)
  /** The plan whose retire dialog is open — refusal or confirm, decided inside. */
  const [retiring, setRetiring] = useState<Plan | null>(null)
  /** A price move on a plan books are paying: held until the count is read. */
  const [confirmingPrice, setConfirmingPrice] = useState<Plan | null>(null)

  const [draftCode, setDraftCode] = useState<PlanCode | null>(null)
  const [draftName, setDraftName] = useState('')
  const [draftPrice, setDraftPrice] = useState<number | undefined>(undefined)
  const [draftAnimals, setDraftAnimals] = useState('')
  const [draftPeople, setDraftPeople] = useState('')
  const [draftStorage, setDraftStorage] = useState('')
  const [draftFeatures, setDraftFeatures] = useState<PlanFeature[]>([])
  const [draftPublic, setDraftPublic] = useState(true)
  const [draftOrder, setDraftOrder] = useState('0')

  const isNew = editing === 'new'
  const plan = editing === 'new' ? null : editing

  /**
   * `planCodeSchema` is a closed enum of four. A code already in use cannot be
   * taken a second time, so the picker offers what is left — plus, when editing,
   * the plan's OWN code, which it is allowed to keep. When nothing is left for a
   * new plan it says so, because a new code is a contract change.
   */
  const takenCodes = new Set((plans.data ?? []).map((row) => row.code))
  const freeCodes = PLAN_CODES.filter((code) => !takenCodes.has(code))
  const codeOptions = plan ? [plan.code, ...freeCodes] : freeCodes

  const closeForm = () => {
    setEditing(null)
    setDraftCode(null)
    setDraftName('')
    setDraftPrice(undefined)
    setDraftAnimals('')
    setDraftPeople('')
    setDraftStorage('')
    setDraftFeatures([])
    setDraftPublic(true)
    setDraftOrder('0')
  }

  const openCreate = () => {
    setDraftCode(freeCodes[0] ?? null)
    setDraftName('')
    setDraftPrice(undefined)
    setDraftAnimals('')
    setDraftPeople('')
    setDraftStorage('')
    setDraftFeatures([])
    setDraftPublic(true)
    setDraftOrder('0')
    setEditing('new')
  }

  /* Every box starts at what the plan actually is, so an untouched form is a
     patch with nothing in it — and the save button says so. */
  const openEdit = (row: Plan) => {
    setDraftCode(row.code)
    setDraftName(row.name)
    setDraftPrice(row.pricePaisa)
    setDraftAnimals(limitText(row.animalLimit))
    setDraftPeople(limitText(row.peopleLimit))
    setDraftStorage(storageText(row.storageGb))
    setDraftFeatures(row.featureCodes)
    setDraftPublic(row.isPublic)
    setDraftOrder(String(row.sortOrder))
    setEditing(row)
  }

  /**
   * A price change reaches every business on the plan at its next renewal, and
   * the revenue tiles, the subscription worklist and the overview all move with
   * it. There is no honest enumeration of that, so everything is refetched.
   */
  const savePlan = useApiMutation<{ id: string; name: string; body: PatchPlanRequest }, ContractPlan>(
    {
      run: (args) => patch<ContractPlan>(`/platform/plans/${args.id}`, args.body),
      invalidateAll: true,
      success: (_result, args) =>
        args.body.pricePaisa === undefined
          ? t('p.plans.planSaved', { name: args.name })
          : t('p.plans.priceChanged', { name: args.name }),
      onDone: closeForm,
    },
  )

  const createPlan = useApiMutation<CreatePlanRequest, ContractPlan>({
    run: (body) => post<ContractPlan>('/platform/plans', body),
    // A brand new plan bills nobody yet; what moves is the plan list and the
    // tiles counted off it.
    invalidates: [queryKeys.plans, queryKeys.platformOverview, queryKeys.subscriptions],
    success: (created) => t('p.plans.planCreated', { name: created.name }),
    onDone: closeForm,
  })

  const retirePlan = useApiMutation<Plan, void>({
    run: (row) => del<void>(`/platform/plans/${row.id}`),
    invalidates: [queryKeys.plans, queryKeys.platformOverview, queryKeys.subscriptions],
    success: (_result, row) => t('p.plans.retired', { name: row.name }),
    onDone: () => setRetiring(null),
  })

  /* Taking a plan off signup is an ordinary patch, so it needs no endpoint of
     its own — which is why it is what a plan in use is offered instead. */
  const hidePlan = useApiMutation<Plan, ContractPlan>({
    run: (row) => patch<ContractPlan>(`/platform/plans/${row.id}`, { isPublic: false }),
    invalidates: [queryKeys.plans],
    success: (_result, row) => t('p.plans.hiddenToast', { name: row.name }),
    onDone: () => setRetiring(null),
  })

  const maxAnimals = parseLimit(draftAnimals)
  const maxUsers = parseLimit(draftPeople)
  const maxStorageBytes = parseStorage(draftStorage)
  const sortOrder = parseOrder(draftOrder)

  const wellFormed =
    draftCode !== null &&
    draftName.trim().length > 0 &&
    draftPrice !== undefined &&
    maxAnimals !== undefined &&
    maxUsers !== undefined &&
    maxStorageBytes !== undefined &&
    sortOrder !== undefined

  /* The patch is a diff — see `planChanges`. An untouched form has nothing to
     save, which is what the disabled button below is saying. */
  const changes: PatchPlanRequest = plan
    ? planChanges(plan, {
        code: draftCode,
        name: draftName,
        pricePaisa: draftPrice,
        maxAnimals,
        maxUsers,
        maxStorageBytes,
        storageText: draftStorage,
        features: draftFeatures,
        isPublic: draftPublic,
        sortOrder,
      })
    : {}

  const changedNothing = !isNew && Object.keys(changes).length === 0
  const saving = createPlan.isPending || savePlan.isPending
  const canSave = wellFormed && !changedNothing && !saving

  const submitEdit = (row: Plan) => savePlan.mutate({ id: row.id, name: row.name, body: changes })

  const submitForm = () => {
    if (!wellFormed || draftCode === null || draftPrice === undefined) return
    if (maxAnimals === undefined || maxUsers === undefined || maxStorageBytes === undefined) return
    if (sortOrder === undefined) return

    if (!plan) {
      createPlan.mutate({
        code: draftCode,
        name: draftName.trim(),
        pricePaisa: draftPrice,
        maxAnimals,
        maxUsers,
        maxStorageBytes,
        features: draftFeatures,
        isPublic: draftPublic,
        sortOrder,
      })
      return
    }

    /* A price that moves on a plan traders are already paying states the count
       before it is allowed to happen. Everything else saves straight away. */
    if (changes.pricePaisa !== undefined && plan.businesses > 0) {
      setConfirmingPrice(plan)
      return
    }
    submitEdit(plan)
  }

  const toggleFeature = (feature: PlanFeature, on: boolean) =>
    setDraftFeatures((current) =>
      on ? [...current, feature] : current.filter((code) => code !== feature),
    )

  return (
    <>
      <PageHeader
        title={t('p.plans.title')}
        subtitle={t('p.plans.subtitle')}
        actions={
          <Button
            variant="primary"
            icon={<Plus className="size-4" />}
            disabled={createPlan.isPending}
            onClick={openCreate}
          >
            {t('p.plans.newPlan')}
          </Button>
        }
      />

      <QueryBoundary query={plans} skeleton={<LoadingSkeleton variant="tiles" />}>
        {(rows) => {
          /* The card the deck highlights is the plan the business actually runs
             on — the one earning the most, not a hard-coded favourite. */
          /* Revenue is money, so it may be absent from the payload entirely.
             A plan whose revenue was not sent is not a plan earning nothing —
             it is one this account may not be told about, so it is left out of
             the ranking rather than ranked at zero. */
          const earning = rows.filter(
            (plan): plan is typeof plan & { revenuePaisa: number } =>
              plan.revenuePaisa !== undefined && plan.revenuePaisa > 0,
          )
          const topRevenue = Math.max(0, ...earning.map((plan) => plan.revenuePaisa))
          const revenue: BarListItem[] = earning
            .sort((a, b) => b.revenuePaisa - a.revenuePaisa)
            .map((plan) => ({
              id: plan.code,
              label: plan.name,
              value: plan.revenuePaisa,
              display: <Money paisa={plan.revenuePaisa} />,
            }))

          return (
            <>
              <div className="mb-[18px] grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
                {rows.map((plan) => (
                  <PlanCard
                    key={plan.code}
                    plan={plan}
                    featured={plan.revenuePaisa === topRevenue && plan.revenuePaisa > 0}
                    onEdit={() => openEdit(plan)}
                  />
                ))}
              </div>

              <div className="grid gap-3.5 lg:grid-cols-2">
                <Section title={t('p.plans.revenueTitle')}>
                  <BarList items={revenue} />
                  <p className="mt-3.5 border-t border-muted/15 pt-3.5 text-[12.5px] leading-relaxed text-sub">
                    {t('p.plans.revenueNote')}
                  </p>
                </Section>

                <Section title={t('p.plans.howTheyPay')}>
                  <QueryBoundary query={mix} skeleton={<LoadingSkeleton variant="list" rows={4} />}>
                    {(methods) => (
                      <BarList
                        items={methods.map((method) => ({
                          id: method.method,
                          label: t(`paymentMethod.${method.method}`),
                          // The bar is head count, not money: the question is
                          // how many traders a rail carries, and one large
                          // customer must not make a rail look busy.
                          value: method.businesses,
                          display: (
                            <>
                              <span className="text-sub">
                                {t('p.plans.businessesCount', { count: method.businesses })}
                              </span>
                              <span className="mx-1.5 text-muted">·</span>
                              {/* Absent for an actor without `platform.billing`
                                  — `<Money>` renders that, never a zero. */}
                              <Money paisa={method.amountPaisa} short />
                            </>
                          ),
                        }))}
                      />
                    )}
                  </QueryBoundary>
                  <p className="mt-3.5 border-t border-muted/15 pt-3.5 text-[12.5px] leading-relaxed text-sub">
                    {t('p.plans.payNote')}
                  </p>
                </Section>
              </div>
            </>
          )
        }}
      </QueryBoundary>

      <Modal
        open={editing !== null}
        onClose={closeForm}
        title={plan ? t('p.plans.editPlan', { name: plan.name }) : t('p.plans.newPlan')}
        subtitle={plan ? t('p.plans.editSubtitle') : t('p.plans.subtitle')}
        footer={
          <>
            {/* One action, always the same words. Whether it can happen is the
                dialog's to answer, because that is where the count is stated —
                a button that changes its own meaning to avoid a refusal teaches
                nobody why the refusal exists. */}
            {plan ? (
              <Button
                variant="ghost"
                icon={<Trash2 className="size-4" />}
                className="text-danger"
                onClick={() => {
                  const row = plan
                  closeForm()
                  setRetiring(row)
                }}
              >
                {t('p.plans.retire')}
              </Button>
            ) : null}
            <Button variant="ghost" onClick={closeForm}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" disabled={!canSave} onClick={submitForm}>
              {t('act.saveChanges')}
            </Button>
          </>
        }
      >
        {draftCode === null ? (
          <EmptyState title={t('p.plans.noCodeLeft')} body={t('p.plans.noCodeLeftBody')} />
        ) : (
          <>
            <Field
              label={t('p.plans.codeLabel')}
              hint={plan ? t('p.plans.codeHintEdit') : t('p.plans.codeHint')}
              htmlFor="new-plan-code"
              required
            >
              <Select<PlanCode>
                id="new-plan-code"
                value={draftCode}
                onChange={setDraftCode}
                options={codeOptions.map((code) => ({
                  value: code,
                  label: t(`p.plan.${code}`),
                }))}
              />
            </Field>
            <Field label={t('label.name')} htmlFor="new-plan-name" required>
              <TextInput id="new-plan-name" value={draftName} onChange={setDraftName} />
            </Field>
            <Field
              label={t('label.amount')}
              hint={t('unit.perMonth')}
              htmlFor="new-plan-amount"
              required
            >
              <MoneyInput id="new-plan-amount" paisa={draftPrice} onChange={setDraftPrice} />
            </Field>
            <Field
              label={t('p.plans.animalLimit')}
              hint={t('p.plans.limitHint')}
              error={maxAnimals === undefined ? t('p.plans.limitInvalid') : undefined}
              htmlFor="new-plan-animals"
            >
              <TextInput id="new-plan-animals" value={draftAnimals} onChange={setDraftAnimals} />
            </Field>
            <Field
              label={t('p.plans.peopleLimit')}
              hint={t('p.plans.limitHint')}
              error={maxUsers === undefined ? t('p.plans.limitInvalid') : undefined}
              htmlFor="new-plan-people"
            >
              <TextInput id="new-plan-people" value={draftPeople} onChange={setDraftPeople} />
            </Field>
            <Field
              label={t('p.plans.storageLimit')}
              hint={t('p.plans.storageHint')}
              error={maxStorageBytes === undefined ? t('p.plans.storageInvalid') : undefined}
              htmlFor="new-plan-storage"
            >
              <TextInput id="new-plan-storage" value={draftStorage} onChange={setDraftStorage} />
            </Field>
            {/* Codes are stored; these labels are looked up. */}
            <Field label={t('p.plans.featuresLabel')}>
              <div className="flex flex-col gap-2">
                {PLAN_FEATURES.map((feature) => (
                  <Toggle
                    key={feature}
                    checked={draftFeatures.includes(feature)}
                    onChange={(on) => toggleFeature(feature, on)}
                    label={t(`p.planFeature.${feature}`)}
                  />
                ))}
              </div>
            </Field>
            {/* `Toggle` carries its own label and hint, so it is not wrapped in
                a `Field` that would print the same words twice. */}
            <div className="mb-4">
              <Toggle
                checked={draftPublic}
                onChange={setDraftPublic}
                label={t('p.plans.publicLabel')}
                hint={t('p.plans.publicHint')}
              />
            </div>
            {/* `sortOrder` is 0 on every plan because nothing ever set it, which
                is why `cheapestFirst` sorts the list by price in the console
                instead. This is the box that was missing. */}
            <Field
              label={t('p.plans.sortLabel')}
              hint={t('p.plans.sortHint')}
              error={sortOrder === undefined ? t('p.plans.sortInvalid') : undefined}
              htmlFor="new-plan-order"
            >
              <TextInput id="new-plan-order" value={draftOrder} onChange={setDraftOrder} />
            </Field>
          </>
        )}
      </Modal>

      {/* Both dialogs render AFTER the form modal on purpose. `Modal` does not
          portal and every overlay is `z-50`, so DOM order is what decides which
          one is on top — declared above the form, the price confirm would open
          underneath it. */}
      {/* A price change is not destructive, but it reaches every business on the
          plan — so it states the count before it is allowed to happen. */}
      <ConfirmDialog
        open={confirmingPrice !== null}
        onClose={() => setConfirmingPrice(null)}
        onConfirm={() => {
          if (confirmingPrice) submitEdit(confirmingPrice)
        }}
        title={
          confirmingPrice ? t('p.plans.priceChangeTitle', { name: confirmingPrice.name }) : ''
        }
        body={
          confirmingPrice
            ? t('p.plans.priceChangeBody', { count: confirmingPrice.businesses })
            : undefined
        }
        confirmLabel={t('act.saveChanges')}
        tone="accent"
        disabled={savePlan.isPending}
      />

      {/*
        One dialog, two answers, because the count that decides which is already
        on screen. A plan nothing is on is retired behind its own code typed
        back; a plan books are paying is not offered a button the server would
        refuse — it is offered the act that is actually wanted.
      */}
      <ConfirmDialog
        open={retiring !== null}
        onClose={() => setRetiring(null)}
        onConfirm={() => {
          if (!retiring) return
          if (retiring.businesses > 0) {
            if (retiring.isPublic) hidePlan.mutate(retiring)
            else setRetiring(null)
          } else {
            retirePlan.mutate(retiring)
          }
        }}
        title={
          retiring === null
            ? ''
            : retiring.businesses > 0
              ? t('p.plans.inUseTitle', { name: retiring.name })
              : t('p.plans.retireTitle', { name: retiring.name })
        }
        body={
          retiring === null
            ? undefined
            : retiring.businesses > 0
              ? retiring.isPublic
                ? t('p.plans.inUseBody', { count: retiring.businesses })
                : t('p.plans.alreadyHidden')
              : t('p.plans.retireBody')
        }
        confirmLabel={
          retiring !== null && retiring.businesses > 0
            ? t('p.plans.hideFromSignup')
            : t('p.plans.retireConfirm')
        }
        tone={retiring !== null && retiring.businesses > 0 ? 'accent' : 'danger'}
        // Typed back only for the destructive branch. Taking a plan off signup
        // is reversible with the toggle in the form above it.
        typeToConfirm={
          retiring !== null && retiring.businesses === 0 ? retiring.code : undefined
        }
        disabled={
          retirePlan.isPending ||
          hidePlan.isPending ||
          (retiring !== null && retiring.businesses > 0 && !retiring.isPublic)
        }
      />
    </>
  )
}
